import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';
import { TooltipElement, Typography } from '@/shared/ui';
import type { FC } from 'react';
import type { RevenueAnalysisSeriesView, RevenueMetric } from '../model/types';

type RevenueAnalysisKpiCardsProps = {
  /** The white leading card: Total Revenue, or Avg Revenue per Resident. */
  headline: { label: string; value: number; info?: string };
  series: RevenueAnalysisSeriesView[];
  metric: RevenueMetric;
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
};

const cardBase =
  'flex min-w-0 flex-1 flex-col gap-1 border p-4 transition-shadow basis-[calc(25%-0.75rem)]';

/**
 * The KPI row above the chart.
 *
 * The affordance in the top-right corner is what the metric changes: a share
 * badge under Revenue, a colour swatch under Revenue per resident — averaging
 * per-resident figures gives a number that does not add up to the headline, so a
 * percentage there would be a lie. The swatch keeps the card↔series link either
 * way.
 */
export const RevenueAnalysisKpiCards: FC<RevenueAnalysisKpiCardsProps> = ({
  headline,
  series,
  metric,
  hoveredKey,
  onHover,
}) => (
  <div className="flex flex-wrap gap-4">
    <div
      data-testid="revenue-analysis-headline-card"
      className={cn(cardBase, 'border-neutral-5 bg-neutral-1')}
    >
      <div className="flex items-center gap-1">
        <Typography variant="body" className="text-neutral-8">
          {headline.label}
        </Typography>
        {headline.info && <TooltipElement content={headline.info} />}
      </div>
      <Typography variant="h3" weight="medium" className="text-neutral-12">
        {formatCurrency(headline.value)}
      </Typography>
    </div>

    {series.map((s) => {
      const isHovered = hoveredKey === s.key;

      return (
        <div
          data-testid="revenue-analysis-series-card"
          key={s.key}
          data-series-key={s.key}
          className={cn(cardBase, 'cursor-default')}
          style={{
            backgroundColor: s.softColor,
            borderColor: s.softColor,
            // The ring takes the series colour, so the highlighted card and the
            // highlighted bar read as the same object.
            boxShadow: isHovered ? `inset 0 0 0 2px ${s.color}` : undefined,
          }}
          onMouseLeave={() => onHover(null)}
          onMouseEnter={() => onHover(s.key)}
        >
          <div className="flex items-start justify-between gap-2">
            <Typography
              variant="body"
              className="min-w-0 truncate"
              style={{ color: s.color }}
            >
              {s.label}
            </Typography>

            {metric === 'revenue' && s.sharePct !== null ? (
              <span
                data-testid="revenue-analysis-share-badge"
                className="text-neutral-1 shrink-0 px-2 py-0.5 text-[12px] leading-[20px]"
                style={{ backgroundColor: s.color }}
              >
                {/* A tiny-but-real series rounds to 0%, which next to a
                    non-zero dollar figure reads as a bug. */}
                {s.sharePct > 0 && s.sharePct < 1
                  ? '<1%'
                  : `${Math.round(s.sharePct)}%`}
              </span>
            ) : (
              <span
                aria-hidden
                className="h-4 w-4 shrink-0"
                style={{ backgroundColor: s.color }}
              />
            )}
          </div>

          <Typography
            variant="h3"
            weight="medium"
            className="truncate"
            style={{ color: s.color }}
          >
            {formatCurrency(s.total)}
          </Typography>
        </div>
      );
    })}
  </div>
);
